import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, forkJoin, from } from 'rxjs';
import { catchError, tap, switchMap, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PaymentData } from 'src/app/model/stripe-models/payment/payment-data';
import { SubscriptionBillsData } from 'src/app/model/stripe-models/subscription/subscription-bill-data';
import { PackageModel } from 'src/app/model/stripe-models/product/package-model';
import { PriceModel } from 'src/app/model/stripe-models/product/price';
import { UserService } from 'src/app/services/user-service/userservice';
import { Bills } from 'src/app/model/stripe-models/subscription/bills';
import { Subscriptions } from 'src/app/model/stripe-models/subscription/Subscribtions';

@Injectable({
  providedIn: 'root'
})
export class StripeService {

  private baseUrl = environment.server.endpoints.baseUrl;
  public Packages: PackageModel[] = [];
  public ProductsToShow: PackageModel[] = [];
  public ProductsToShowSubject: BehaviorSubject<PackageModel[]>;
  public ProductsToShowObservable: Observable<PackageModel[]>;
  public loading = true;
  private productsProcessed = 0;
  private totalProducts = 0;
  private loadingPromise: Promise<void> | null = null;
  loggedIn = false;

  constructor(private httpClient: HttpClient, private userService: UserService) {
    this.ProductsToShow = [];
    this.ProductsToShowSubject = new BehaviorSubject<PackageModel[]>(this.ProductsToShow);
    this.ProductsToShowObservable = this.ProductsToShowSubject.asObservable();
    this.userService.currentUserObservable.subscribe((user) => {
      if (user && this.userService.loggedIn) {
        this.loggedIn = this.userService.loggedIn;
      } else { this.loggedIn = false; }
    });
    this.loadProducts();
  }

  public loadProducts(): Promise<void> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.setLoadingState(true);
    this.loadingPromise = new Promise<void>((resolve) => {
      this.getAllProducts().then(productsObs => {
        productsObs.pipe(
          switchMap((data: any) => {
            if (!data || data.length === 0) {
              return of([]);
            }
            this.totalProducts = data.filter((item: any) => item.active).length;
            if (this.totalProducts === 0) {
              return of([]);
            }

            const activeProducts = data.filter((item: any) => item.active);
            const tasks = activeProducts.map((item: any) => {
              return from(this.getPriceByProductID(item.id)).pipe(
                switchMap(pricesObs => pricesObs),
                switchMap((productPrices: any) => {
                  const yearlyPrices = productPrices.filter((price: any) =>
                    price.recurring && price.recurring.interval === 'year'
                  );
                  if (yearlyPrices.length === 0) {
                    return of(null);
                  }
                  const productPrice = yearlyPrices[0];
                  const priceRecurring = productPrice.recurring;
                  const price: PriceModel = {
                    id: productPrice.id,
                    currency: productPrice.currency,
                    unit_amount: productPrice.unit_amount || productPrice.unitAmount,
                    recurring: {
                      interval: priceRecurring.interval,
                      interval_count: priceRecurring.interval_count || priceRecurring.intervalCount,
                      usage_type: priceRecurring.usage_type || priceRecurring.usageType
                    }
                  };

                  return from(this.getFeaturesByProductID(item.id)).pipe(
                    switchMap(featuresObs => featuresObs),
                    map((productFeatures: any) => {
                      const packageColor = this.getColorForPackage(item.name);
                      const packageModel: PackageModel = {
                        packageClass: item.name,
                        active: item.active,
                        images: item.images,
                        productId: item.id,
                        productDescription: item.description,
                        productName: item.name,
                        priceId: price,
                        unitAmount: price.unit_amount,
                        listItems: productFeatures || [],
                        color: packageColor,
                        title: item.metadata.title
                      };
                      return packageModel;
                    }),
                    catchError(() => of(null))
                  );
                }),
                catchError(() => of(null))
              );
            });
            return forkJoin(tasks);
          }),
          catchError(() => of([]))
        ).subscribe((results: any[]) => {
          this.Packages = results.filter(r => r !== null && r.active);
          this.updateProductsToShow();
          this.setLoadingState(false);
          this.loadingPromise = null;
          resolve();
        }, (error) => {
          this.setLoadingState(false);
          this.loadingPromise = null;
          resolve();
        });
      }).catch(() => {
        this.setLoadingState(false);
        this.loadingPromise = null;
        resolve();
      });
    });
    return this.loadingPromise;
  }

  checkIfAllProductsProcessed(): void {
    this.productsProcessed++;
    if (this.productsProcessed >= this.totalProducts) {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading: boolean): void {
    this.loading = isLoading;
  }

  getColorForPackage(packageName: string): string {
    switch (packageName) {
      case 'Basic': return '#64a596';
      case 'Starter': return '#80a92d';
      case 'Professional': return '#d36d70';
      case 'Enterprise': return '#025b5a';
      default: return '#64a596';
    }
  }

  updateProductsToShow(): void {
    this.ProductsToShow = this.Packages.filter(pkg =>
      pkg.priceId && pkg.priceId.recurring && pkg.priceId.recurring.interval === 'year'
    );
    this.sort_by_key(this.ProductsToShow, 'unitAmount');

    this.ProductsToShowSubject.next(this.ProductsToShow);
  }

  sort_by_key(array: any[], key: string): any[] {
    return array.sort((a, b) => {
      let x = a[key];
      let y = b[key];
      return ((x < y) ? -1 : ((x > y) ? 1 : 0));
    });
  }

  /*
  addStripeUser(stripeUser: StripeUser): Observable<string | {message: string, status: number}> {
    if(stripeUser.address == null) stripeUser.address = {};
    if(stripeUser.description == null) stripeUser.description = "";
    if(stripeUser.phone == null) stripeUser.phone = "";
    if(stripeUser.shipping == null) stripeUser.shipping = {};
    let addUserEndPoint = this.baseUrl+ environment.server.endpoints.stripe.addStripeUser;
    return this.httpClient.post<string>(addUserEndPoint, stripeUser, this.getHeaders(null)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 404 || status === 409? response : "Server internal error";
        return of({message: serverMessage, status: status});

      })
    );
  }
    */

  public validateCoupon(code: string, orgId: number): Observable<string> {
    const endpoint = this.baseUrl + environment.server.endpoints.stripe.validateCoupon;
    const params = new HttpParams()
      .set('coupon', code)
      .set('orgId', orgId.toString());
    return this.httpClient.get(endpoint, { params, responseType: 'text' }).pipe(
      tap(res => {
        return res;
      }),
      catchError(err => {
        return of('invalid');
      })
    );
  }

  public couponDiscount(code: string, orgId: number): Observable<string> {
    const endpoint = this.baseUrl + environment.server.endpoints.stripe.couponDiscount;
    const params = new HttpParams()
      .set('coupon', code)
      .set('orgId', orgId.toString());
    return this.httpClient.get(endpoint, { params, responseType: 'text' }).pipe(
      tap(res => {
        return res;
      }),
      catchError(err => {
        return of('invalid');
      })
    );
  }

  public updateStripeUserDateBecauseNewPayment(orgId: number): Observable<string | { message: string, status: number }> {
    let params = { "orgId": orgId.toString() };
    let queryParams = new HttpParams({ fromObject: params });
    let updateUserEndPoint = this.baseUrl + environment.server.endpoints.stripe.updateStripeUserNewPayment;
    return this.httpClient.patch<string>(updateUserEndPoint, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public addSubscription(userId: string, orgId: string, plan: string, quantity: string, paymentMethodId: string, voucherCode?: string): Observable<string | { message: string, status: number }> {
    let params: Record<string, string> = { "userId": userId, "orgId": orgId, "plan": plan, "quantity": quantity, "paymentMethod": paymentMethodId };
    if (voucherCode?.trim()) {
      params.coupon = voucherCode.trim();
    }
    let queryParams = new HttpParams({ fromObject: params });
    let addSubscriptionEndPoint = this.baseUrl + environment.server.endpoints.stripe.addSubscription;
    return this.httpClient.post<string>(addSubscriptionEndPoint, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public registerSubscriptionInDB(orgId: number): Observable<string | { message: string, status: number }> {
    let params = { "orgId": orgId.toString() };
    let queryParams = new HttpParams({ fromObject: params });
    let registerSubscriptionEndPoint = this.baseUrl + environment.server.endpoints.stripe.registerSubscriptionInDB;

    return this.httpClient.post<string>(registerSubscriptionEndPoint, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public getSubscriptionData(orgId: string, paginationDirectionSub: string, limit: string): Observable<Subscriptions | { message: string, status: number }> {
    let params: { "orgId": string, "beforeAfterSubId"?: string, "limit": string } = { "orgId": orgId, "limit": limit };
    if (paginationDirectionSub && paginationDirectionSub.length > 0) params.beforeAfterSubId = paginationDirectionSub;
    let queryParams = new HttpParams({ fromObject: params });
    let subscriptionDataEndPoint = this.baseUrl + environment.server.endpoints.stripe.subscriptionData;
    return this.httpClient.get<SubscriptionBillsData>(subscriptionDataEndPoint, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public editSubscription(orgId: string, subscriptionId: string, paymentMethodId: string): Observable<string | { message: string, status: number }> {
    let params = { "orgId": orgId, "subscriptionId": subscriptionId, "paymentMethodId": paymentMethodId };
    let queryParams = new HttpParams({ fromObject: params });
    let editSubscriptionEndpoint = this.baseUrl + environment.server.endpoints.stripe.updateSubscription;
    return this.httpClient.put<string>(editSubscriptionEndpoint, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 400 || status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public cancelSubscription(orgId: string, subscriptionId: string): Observable<string | { message: string, status: number }> {
    let params = { "orgId": orgId, "subscriptionId": subscriptionId };
    let queryParams = new HttpParams({ fromObject: params });
    let cancelSubscriptionEndpoint = this.baseUrl + environment.server.endpoints.stripe.cancelSubscription;
    return this.httpClient.put<string>(cancelSubscriptionEndpoint, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public resumeSubscription(orgId: string, subscriptionId: string): Observable<string | { message: string, status: number }> {
    let params = { "orgId": orgId, "subscriptionId": subscriptionId };
    let queryParams = new HttpParams({ fromObject: params });
    let resumeSubscriptionEndpoint = this.baseUrl + environment.server.endpoints.stripe.resumeSubscription;
    return this.httpClient.put<string>(resumeSubscriptionEndpoint, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public getSubscriptionDataSize(orgId: string): Observable<string | { message: string, status: number }> {
    let params: { "orgId": string } = { "orgId": orgId };
    let queryParams = new HttpParams({ fromObject: params });
    let subscriptionDataSizeEndPoint = this.baseUrl + environment.server.endpoints.stripe.subscriptionDataSize;
    return this.httpClient.get<string>(subscriptionDataSizeEndPoint, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public getBillingData(orgId: string, paginationDirectionBill: string, limit: string): Observable<Bills | { message: string, status: number }> {
    let params: { "orgId": string, "beforeAfterBillId"?: string, "limit": string } = { "orgId": orgId, "limit": limit };
    if (paginationDirectionBill && paginationDirectionBill.length > 0) params.beforeAfterBillId = paginationDirectionBill;
    let queryParams = new HttpParams({ fromObject: params });
    let billingDataEndPoint = this.baseUrl + environment.server.endpoints.stripe.billingData;
    return this.httpClient.get<Bills>(billingDataEndPoint, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public getBillingDataSize(orgId: string): Observable<string | { message: string, status: number }> {
    let params: { "orgId": string } = { "orgId": orgId };
    let queryParams = new HttpParams({ fromObject: params });
    let billingDataSizeEndPoint = this.baseUrl + environment.server.endpoints.stripe.billingDataSize;
    return this.httpClient.get<string>(billingDataSizeEndPoint, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public getListingCurrentUsage(orgId: string, subscriptionId: string): Observable<any | { message: string, status: number }> {
    let params: { "orgId": string, "subscriptionId": string } = { "orgId": orgId, "subscriptionId": subscriptionId };
    let queryParams = new HttpParams({ fromObject: params });
    let ListingCurrentUsage = this.baseUrl + environment.server.endpoints.stripe.getCurrentUsage;
    return this.httpClient.get(ListingCurrentUsage, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public getProductsFromListing(orgId: string, subscriptionId: string): Observable<any | { message: string, status: number }> {
    let params: { "orgId": string, "subscriptionId": string } = { "orgId": orgId, "subscriptionId": subscriptionId };
    let queryParams = new HttpParams({ fromObject: params });
    let ProductsFromListing = this.baseUrl + environment.server.endpoints.stripe.getProductsFromListing;
    return this.httpClient.get(ProductsFromListing, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public getAllListingProductFromOrgId(orgId: string): Observable<any | { message: string, status: number }> {
    let params: { "orgId": string } = { "orgId": orgId };
    let queryParams = new HttpParams({ fromObject: params });
    let allListingProductFromOrgId = this.baseUrl + environment.server.endpoints.stripe.getListings;
    return this.httpClient.get(allListingProductFromOrgId, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public addProductsToListing(orgId: string, subscriptionId: string, productId: string): Observable<any | { message: string, status: number }> {
    let params: { "orgId": string, "subscriptionId": string, "productId": string, } = { "orgId": orgId, "subscriptionId": subscriptionId, "productId": productId };
    let queryParams = new HttpParams({ fromObject: params });
    let addProductsToListing = this.baseUrl + environment.server.endpoints.stripe.addProductToListing;
    return this.httpClient.put(addProductsToListing, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public removeProductFromListing(orgId: string, subscriptionId: string, productId: string): Observable<any | { message: string, status: number }> {
    let params: { "orgId": string, "subscriptionId": string, "productId": string, } = { "orgId": orgId, "subscriptionId": subscriptionId, "productId": productId };
    let queryParams = new HttpParams({ fromObject: params });
    let removeProductFromListing = this.baseUrl + environment.server.endpoints.stripe.removeProductFromListing;
    return this.httpClient.put(removeProductFromListing, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public prioritizeProduct(orgId: string, subscriptionId: string, productId: string, prioritize: boolean = true): Observable<any | { message: string; status: number }> {
    const params = {
      orgId: orgId,
      subscriptionId: subscriptionId,
      productId: productId,
      prioritize: prioritize.toString()
    };
    const queryParams = new HttpParams({ fromObject: params });
    const url = this.baseUrl + environment.server.endpoints.stripe.prioritizeProduct;

    return this.httpClient.put(url, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        const status = err.status;
        const response = err.error?.message || err.error;
        const serverMessage =
          status === 401 || status === 404 || status === 409 || status === 502
            ? response
            : 'Server internal error';
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public isProductPrioritized(orgId: string, productId: string): Observable<boolean | { message: string; status: number }> {
    const params = {
      orgId: orgId,
      productId: productId
    };
    const queryParams = new HttpParams({ fromObject: params });
    const url = this.baseUrl + environment.server.endpoints.stripe.isProductPrioritized;

    return this.httpClient.get<boolean>(url, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        const status = err.status;
        const response = err.error?.message || err.error;
        const serverMessage =
          status === 401 || status === 404 || status === 409 || status === 502
            ? response
            : 'Server internal error';
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public addPaymentMethod(orgId: string, returnUrl?: string): Observable<string | { message: string, status: number }> {
    let successUrl: string;
    if (returnUrl?.includes('tarife')) {
      successUrl = `${environment.server.stripe.addPaymentMethodUrl.successUrl}tarife?showDialog=true`;
    } else if (returnUrl) {
      successUrl = `${environment.server.stripe.addPaymentMethodUrl.successUrl}organisations/${orgId}/${returnUrl}`;
    } else {
      successUrl = `${environment.server.stripe.addPaymentMethodUrl.successUrl}organisations/${orgId}/zahlung`;
    }
    let cancelUrl = environment.server.stripe.addPaymentMethodUrl.cancelUrl + orgId + '/zahlung';
    let params = { "orgId": orgId, "successUrl": successUrl, "cancelUrl": cancelUrl };
    let queryParams = new HttpParams({ fromObject: params });
    let addPaymentMethodEndPoint = this.baseUrl + environment.server.endpoints.stripe.addPaymentMethod;
    return this.httpClient.post<string>(addPaymentMethodEndPoint, null, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public getPaymentData(orgId: string, cardId: string): Observable<PaymentData[] | PaymentData | { message: string, status: number }> {
    let params: { "orgId": string, "cardId"?: string } = { "orgId": orgId };
    if (cardId && cardId.length > 0) params.cardId = cardId;
    let queryParams = new HttpParams({ fromObject: params });
    let paymentDataEndpoint = this.baseUrl + environment.server.endpoints.stripe.paymentData;
    return this.httpClient.get<PaymentData[] | PaymentData>(paymentDataEndpoint, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public checkPaymentMethodToDelete(orgId: string, paymentMethodId: string): Observable<string | { message: string, status: number }> {
    let params = { "orgId": orgId, "paymentMethodId": paymentMethodId };
    let queryParams = new HttpParams({ fromObject: params });
    let deletionCheckEndpoint = this.baseUrl + environment.server.endpoints.stripe.checkPaymentMethodToDelete;
    return this.httpClient.get<string>(deletionCheckEndpoint, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  public deletePaymentData(orgId: string, toDeletePaymentMethodId: string, replacementPaymentMethodId: string): Observable<string | { message: string, status: number }> {
    let params: { "orgId"?: string, "toDeletePaymentMethodId": string, "replacementPaymentMethodId"?: string } = { "toDeletePaymentMethodId": toDeletePaymentMethodId };
    if (orgId && orgId.length > 0) {
      params["orgId"] = orgId;
      params["replacementPaymentMethodId"] = replacementPaymentMethodId;
    }
    let queryParams = new HttpParams({ fromObject: params });
    let deletePaymentDataEndpoint = this.baseUrl + environment.server.endpoints.stripe.deletePaymentMethod;
    return this.httpClient.delete<string>(deletePaymentDataEndpoint, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  async pay(paymentdto: any): Promise<void> {
    this.httpClient.post(`${this.baseUrl}/stripe/payment`, paymentdto, this.getHeaders(null))
      .subscribe((data: any) => { window.location.href = data.id; });
  }

  public addAndPay(paymentdto: any): Observable<any> {
    return this.httpClient.post(`${this.baseUrl}/stripe/payment`, paymentdto, this.getHeaders(null));
  }

  async getAllProducts(): Promise<Observable<any>> {
    return await this.httpClient.get(`${this.baseUrl}/stripe/products`);
  }

  async getPriceByProductID(productId: string): Promise<Observable<any>> {
    let params: { "productId"?: string } = { "productId": productId };
    let queryParams = new HttpParams({ fromObject: params });
    return await this.httpClient.get<string>(`${this.baseUrl}/stripe/productPrices`, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  async getFeaturesByProductID(productId: string): Promise<Observable<any>> {
    let params: { "productId"?: string } = { "productId": productId };
    let queryParams = new HttpParams({ fromObject: params });
    return await this.httpClient.get(`${this.baseUrl}/stripe/productFeatures`, this.getHeaders(queryParams)).pipe(
      catchError((err) => {
        let status = err.status;
        let response = err.error?.message || err.error;
        let serverMessage = status === 401 || status === 404 || status === 502 ? response : "Server internal error";
        return of({ message: serverMessage, status: status });
      })
    );
  }

  private getHeaders(params: HttpParams | null): { params?: HttpParams, withCredentials: boolean } {
    return params != null ? { params: params, withCredentials: true } : { withCredentials: true };
  }
}
